import { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { apiFetch, ApiError } from '../../services/api-client';

// Content panel for a looked-up "lite" card (walletless, shop-created member —
// see api/_lib/lite.ts). Rendered inside ScanPassModal's counter-scan flow
// (Task 7) and reused by Counter Book (Task 8); it owns no modal chrome of
// its own, just the member actions: rename, stamp, redeem, one-time paper
// card trade-in, and linking the lite card to a real wallet pass.

export interface LiteMember {
  id: string;
  card_code: string;
  display_name: string | null;
  stamps: number;
  trade_in_at: string | null;
  migrated_at: string | null;
  last_visit_at: string | null;
}

interface Props {
  member: LiteMember;
  onDone: () => void;
}

const CARD_SIZE = 10; // mirrors CARD_SIZE in api/_lib/lite.ts
const TRADE_IN_CAP = CARD_SIZE - 1;

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

// Server error codes → counter-friendly copy. Raw server strings (Postgres
// messages, code-style identifiers) never reach the screen: anything not in
// this map falls back to the caller's generic ALL-CAPS message.
const ERROR_COPY: Record<string, string> = {
  insufficient_stamps: 'NOT ENOUGH STAMPS',
  already_traded_in: 'ALREADY TRADED IN',
  member_migrated: 'ALREADY LINKED TO APP',
  already_migrated: 'ALREADY LINKED',
  pass_is_for_another_shop: 'PASS IS FOR ANOTHER SHOP',
  member_not_found: 'CARD NOT FOUND',
  not_shop_owner: 'NOT YOUR SHOP',
  rate_limited: 'TOO MANY ACTIONS — WAIT A MOMENT',
};

export function liteErrorCopy(e: unknown, fallback: string): string {
  return e instanceof ApiError && ERROR_COPY[e.message] ? ERROR_COPY[e.message] : fallback;
}

export default function LiteMemberPanel({ member, onDone }: Props) {
  const [current, setCurrent] = useState(member);

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(member.display_name ?? '');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const [quantity, setQuantity] = useState(1);
  const [stampBusy, setStampBusy] = useState(false);
  const [stampError, setStampError] = useState<string | null>(null);

  const [redeemCost, setRedeemCost] = useState(String(CARD_SIZE));
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const [tradeInStamps, setTradeInStamps] = useState(String(TRADE_IN_CAP));
  const [tradeInBusy, setTradeInBusy] = useState(false);
  const [tradeInError, setTradeInError] = useState<string | null>(null);

  const [linking, setLinking] = useState(false);
  const [linkMoved, setLinkMoved] = useState<number | null>(null);

  // Stable identity so the nested LinkScanner's camera effect (deps include
  // this callback) doesn't tear down and restart the stream just because
  // staff touched an unrelated stamp/redeem control while it's open.
  const handleMoved = useCallback((moved: number) => {
    setLinking(false);
    setLinkMoved(moved);
    setCurrent((c) => ({ ...c, migrated_at: new Date().toISOString() }));
  }, []);

  // Reset local state whenever a different member is handed in (Counter Book
  // reuses this panel across rows without unmounting between taps).
  useEffect(() => {
    setCurrent(member);
    setEditingName(false);
    setNameInput(member.display_name ?? '');
    setRenameError(null);
    setStampError(null);
    setRedeemError(null);
    setTradeInError(null);
    setLinking(false);
    setLinkMoved(null);
  }, [member]);

  async function saveName() {
    setRenameBusy(true);
    setRenameError(null);
    try {
      await apiFetch('/api/stamp', {
        action: 'lite-rename',
        memberId: current.id,
        displayName: nameInput,
      });
      setCurrent((c) => ({ ...c, display_name: nameInput.trim() || null }));
      setEditingName(false);
    } catch (e) {
      setRenameError(liteErrorCopy(e, 'COULD NOT SAVE NAME'));
    } finally {
      setRenameBusy(false);
    }
  }

  async function doStamp() {
    setStampBusy(true);
    setStampError(null);
    try {
      const r = await apiFetch<{ ok: true; stamps: number }>('/api/stamp', {
        action: 'lite-stamp',
        memberId: current.id,
        quantity,
      });
      setCurrent((c) => ({ ...c, stamps: r.stamps }));
    } catch (e) {
      setStampError(liteErrorCopy(e, 'STAMP FAILED — TRY AGAIN'));
    } finally {
      setStampBusy(false);
    }
  }

  async function doRedeem() {
    // Belt-and-braces: the input clamps on change, but clamp again on submit
    // so a stale/programmatic value can never post out of the 1-100 range.
    const cost = clamp(Number(redeemCost) || 0, 1, 100);
    setRedeemBusy(true);
    setRedeemError(null);
    try {
      const r = await apiFetch<{ ok: true; stamps: number }>('/api/stamp', {
        action: 'lite-redeem',
        memberId: current.id,
        cost,
      });
      setCurrent((c) => ({ ...c, stamps: r.stamps }));
    } catch (e) {
      setRedeemError(liteErrorCopy(e, 'REDEEM FAILED — TRY AGAIN'));
    } finally {
      setRedeemBusy(false);
    }
  }

  async function doTradeIn() {
    const stamps = clamp(Number(tradeInStamps) || 0, 1, TRADE_IN_CAP);
    setTradeInBusy(true);
    setTradeInError(null);
    try {
      const r = await apiFetch<{ ok: true; stamps: number }>('/api/stamp', {
        action: 'lite-trade-in',
        memberId: current.id,
        stamps,
      });
      setCurrent((c) => ({ ...c, stamps: r.stamps, trade_in_at: new Date().toISOString() }));
    } catch (e) {
      setTradeInError(liteErrorCopy(e, 'TRADE-IN FAILED — TRY AGAIN'));
    } finally {
      setTradeInBusy(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        {editingName ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="flex-1 min-w-0 bg-white rounded-full px-[16px] py-[10px] font-grotesk font-medium text-sm text-primary border-2 border-hairline focus:border-coral outline-none"
            />
            <button
              onClick={saveName}
              disabled={renameBusy}
              className="shrink-0 rounded-full bg-pixel-cyan text-teal-ink font-grotesk font-bold text-[12px] py-[10px] px-4 disabled:opacity-50"
            >
              {renameBusy ? 'SAVING…' : 'SAVE'}
            </button>
            <button
              onClick={() => { setEditingName(false); setNameInput(current.display_name ?? ''); }}
              className="shrink-0 font-grotesk font-bold text-[12px] text-body-grey hover:text-primary"
            >
              CANCEL
            </button>
          </div>
        ) : (
          <>
            <p className="font-grotesk font-bold text-[19px] text-primary truncate">
              {current.display_name || 'UNNAMED CARD'}
            </p>
            <button
              onClick={() => setEditingName(true)}
              className="shrink-0 font-grotesk font-bold text-[12px] text-body-grey hover:text-coral"
            >
              EDIT
            </button>
          </>
        )}
      </div>
      {renameError && <p className="font-grotesk font-medium text-[13px] text-error mt-1">{renameError}</p>}

      <div className="text-center my-4">
        <p data-testid="lite-stamp-balance" className="font-grotesk font-bold text-4xl text-[#2f9440]">
          {current.stamps}
        </p>
        <p className="font-heading text-[8px] text-body-grey tracking-[1px] mt-1">STAMPS</p>
      </div>

      {/* Stamp */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="font-heading text-[8px] text-body-grey">STAMPS</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setQuantity(n)}
              className={`w-6 h-6 rounded-full font-grotesk font-bold text-[12px] border-2 ${
                quantity === n ? 'border-coral text-coral' : 'border-hairline text-body-grey'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <button
          onClick={doStamp}
          disabled={stampBusy}
          className="shrink-0 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-2.5 px-5 disabled:opacity-50"
        >
          {stampBusy ? 'STAMPING…' : 'STAMP'}
        </button>
      </div>
      {stampError && <p className="font-grotesk font-medium text-[13px] text-error mt-1">{stampError}</p>}

      {/* Redeem */}
      <div className="mt-3 pt-3 border-t border-hairline flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="font-heading text-[8px] text-body-grey">COST</span>
          <input
            aria-label="Redeem cost"
            type="number"
            min={1}
            max={100}
            value={redeemCost}
            onChange={(e) => setRedeemCost(String(clamp(Number(e.target.value) || 0, 1, 100)))}
            className="w-12 bg-white border-2 border-hairline rounded-[8px] font-grotesk font-bold text-[12px] text-primary text-center py-1"
          />
        </div>
        <button
          onClick={doRedeem}
          disabled={redeemBusy}
          className="shrink-0 rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-2.5 px-4 disabled:opacity-50"
        >
          {redeemBusy ? 'REDEEMING…' : `REDEEM (${redeemCost || CARD_SIZE})`}
        </button>
      </div>
      {redeemError && <p className="font-grotesk font-medium text-[13px] text-error mt-1">{redeemError}</p>}

      {/* Paper card trade-in — one-shot, disappears once used */}
      {!current.trade_in_at && (
        <div className="mt-3 pt-3 border-t border-hairline">
          <p className="font-heading text-[8px] text-gold-ink tracking-[1px] mb-2">PAPER CARD TRADE-IN</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <label htmlFor="lite-trade-in-stamps" className="font-heading text-[8px] text-body-grey">
                STAMPS
              </label>
              <input
                id="lite-trade-in-stamps"
                aria-label="Trade-in stamps"
                type="number"
                min={1}
                max={TRADE_IN_CAP}
                value={tradeInStamps}
                onChange={(e) => setTradeInStamps(String(clamp(Number(e.target.value) || 0, 1, TRADE_IN_CAP)))}
                className="w-12 bg-white border-2 border-hairline rounded-[8px] font-grotesk font-bold text-[12px] text-primary text-center py-1"
              />
            </div>
            <button
              onClick={doTradeIn}
              disabled={tradeInBusy}
              className="shrink-0 rounded-full bg-accent-alt text-primary font-grotesk font-bold text-[13px] py-2.5 px-4 disabled:opacity-50"
            >
              {tradeInBusy ? 'APPLYING…' : 'TRADE IN'}
            </button>
          </div>
          {tradeInError && <p className="font-grotesk font-medium text-[13px] text-error mt-1">{tradeInError}</p>}
        </div>
      )}

      {/* Link the lite card to a real wallet pass */}
      <div className="mt-3 pt-3 border-t border-hairline">
        {linking ? (
          <LinkScanner
            memberId={current.id}
            onCancel={() => setLinking(false)}
            onMoved={handleMoved}
          />
        ) : current.migrated_at ? (
          <p className="font-heading text-[9px] text-body-grey">LINKED TO APP</p>
        ) : (
          <button
            onClick={() => { setLinkMoved(null); setLinking(true); }}
            className="w-full rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-sm py-3"
          >
            LINK TO APP
          </button>
        )}
        {linkMoved !== null && (
          <p className="font-heading text-[9px] text-[#2f9440] mt-2">MOVED {linkMoved} STAMPS TO THE APP</p>
        )}
      </div>

      <button onClick={onDone} className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 mt-4">
        DONE
      </button>
    </div>
  );
}

// Nested scanner for the LINK TO APP flow: scans the member's NEW
// bitbrew:v1 wallet pass and moves the lite balance onto it (lite-migrate).
// Self-contained rather than sharing ScanPassModal's scan loop — this fires
// rarely (one-time per member) and duplicating the ~40 lines of
// BarcodeDetector/jsQR wiring is cheaper than coupling two independent
// scan lifecycles to one shared effect.
function LinkScanner({
  memberId,
  onMoved,
  onCancel,
}: {
  memberId: string;
  onMoved: (moved: number) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<'scanning' | 'sending' | 'error'>('scanning');
  const [message, setMessage] = useState('');
  const busyRef = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let cancelled = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Detector = (window as any).BarcodeDetector;
    const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null;
    let lastScan = 0;

    async function onCode(raw: string) {
      if (busyRef.current) return;
      if (!raw.startsWith('bitbrew:v1:')) return; // ignore foreign QRs, keep scanning
      busyRef.current = true;
      setStatus('sending');
      try {
        const r = await apiFetch<{ ok: true; moved: number }>('/api/stamp', {
          action: 'lite-migrate',
          memberId,
          code: raw,
        });
        onMoved(r.moved);
      } catch (e) {
        busyRef.current = false;
        setStatus('error');
        setMessage(liteErrorCopy(e, 'LINK FAILED — TRY AGAIN'));
      }
    }

    async function tick(now: number) {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2 && now - lastScan > 120 && !busyRef.current) {
        lastScan = now;
        try {
          if (detector) {
            const codes = await detector.detect(video);
            if (codes[0]?.rawValue) await onCode(codes[0].rawValue);
          } else if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const hit = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (hit?.data) await onCode(hit.data);
          }
        } catch { /* keep scanning */ }
      }
      raf = requestAnimationFrame(tick);
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      setMessage('Camera unavailable — allow camera access and retry');
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        raf = requestAnimationFrame(tick);
      })
      .catch(() => { setStatus('error'); setMessage('Camera unavailable — allow camera access and retry'); });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [memberId, onMoved]);

  return (
    <div>
      <p className="font-heading text-[9px] text-coral tracking-[1px] mb-2">SCAN THEIR NEW PASS</p>
      {status === 'error' ? (
        <p className="font-grotesk font-medium text-[13px] text-error">{message}</p>
      ) : (
        <>
          {/* Camera preview stays dark — functional, not decorative chrome. */}
          <video ref={videoRef} playsInline muted className="w-full aspect-square object-cover bg-primary rounded-[10px]" />
          <p className="font-grotesk font-medium text-[12px] text-body-grey mt-2">
            {status === 'sending' ? 'LINKING…' : 'Point at their new wallet-pass QR'}
          </p>
        </>
      )}
      <button onClick={onCancel} className="font-grotesk font-bold text-[12px] text-body-grey hover:text-primary mt-3">
        CANCEL
      </button>
    </div>
  );
}
